import * as React from 'react'
import { sx as sxFunc, SxProps } from '../sx'
import clsx from 'clsx'
import styles from './index.module.scss'
import { capitalize } from '../../utils'

export interface LinearProgressClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root and bar2 element if `color="primary"`; bar2 if `variant="buffer"`. */
  colorPrimary: string
  /** Styles applied to the root and bar2 elements if `color="secondary"`; bar2 if `variant="buffer"`. */
  colorSecondary: string
  /** Styles applied to the root element if `variant="determinate"`. */
  determinate: string
  /** Styles applied to the root element if `variant="indeterminate"`. */
  indeterminate: string
  /** Styles applied to the root element if `variant="buffer"`. */
  buffer: string
  /** Styles applied to the root element if `variant="query"`. */
  query: string
  /** Styles applied to the additional bar element if `variant="buffer"`. */
  dashed: string
  /** Styles applied to the additional bar element if `variant="buffer"` and `color="primary"`. */
  dashedColorPrimary: string
  /** Styles applied to the additional bar element if `variant="buffer"` and `color="secondary"`. */
  dashedColorSecondary: string
  /** Styles applied to the layered bar1 and bar2 elements. */
  bar: string
  /** Styles applied to the bar elements if `color="primary"`; bar2 if `variant` not "buffer". */
  barColorPrimary: string
  /** Styles applied to the bar elements if `color="secondary"`; bar2 if `variant` not "buffer". */
  barColorSecondary: string
  /** Styles applied to the bar1 element if `variant="indeterminate or query"`. */
  bar1Indeterminate: string
  /** Styles applied to the bar1 element if `variant="determinate"`. */
  bar1Determinate: string
  /** Styles applied to the bar1 element if `variant="buffer"`. */
  bar1Buffer: string
  /** Styles applied to the bar2 element if `variant="indeterminate or query"`. */
  bar2Indeterminate: string
  /** Styles applied to the bar2 element if `variant="buffer"`. */
  bar2Buffer: string
}

export interface LinearProgressProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<LinearProgressClasses>
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'inherit'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The value of the progress indicator for the determinate and buffer variants.
   * Value between 0 and 100.
   */
  value?: number
  /**
   * The value for the buffer variant.
   * Value between 0 and 100.
   */
  valueBuffer?: number
  /**
   * The variant to use.
   * Use indeterminate or query when there is no progress value.
   * @default 'indeterminate'
   */
  variant?: 'determinate' | 'indeterminate' | 'buffer' | 'query'
  /**
   * The thickness of the progress lines.
   * @default 3.6
   */
  thickness?: number
}
/**
 * ## ARIA
 *
 * If the progress bar is describing the loading progress of a particular region of a page,
 * you should use `aria-describedby` to point to the progress bar, and set the `aria-busy`
 * attribute to `true` on that region until it has finished loading.
 */

const direction = 'ltr'

export const LinearProgress = React.forwardRef<
  HTMLSpanElement,
  LinearProgressProps
>(function LinearProgress(props, ref) {
  const {
    className,
    color = 'primary',
    value,
    valueBuffer,
    variant = 'indeterminate',
    classes,
    thickness = 4,
    sx,
    ...other
  } = props

  const rootProps: any = {}
  const inlineStyles: { bar1: any; bar2: any } = { bar1: {}, bar2: {} }

  if (variant === 'determinate' || variant === 'buffer') {
    if (value !== undefined) {
      rootProps['aria-valuenow'] = Math.round(value)
      rootProps['aria-valuemin'] = 0
      rootProps['aria-valuemax'] = 100
      let transform = value - 100
      // @ts-ignore
      if (direction === 'rtl') {
        transform = -transform
      }
      inlineStyles.bar1.transform = `translateX(${transform}%)`
    } else if (process.env.NODE_ENV !== 'production') {
      console.error(
        'You need to provide a value prop ' +
          'when using the determinate or buffer variant of LinearProgress .'
      )
    }
  }
  if (variant === 'buffer') {
    if (valueBuffer !== undefined) {
      let transform = (valueBuffer || 0) - 100
      // @ts-ignore
      if (direction === 'rtl') {
        transform = -transform
      }
      inlineStyles.bar2.transform = `translateX(${transform}%)`
    } else if (process.env.NODE_ENV !== 'production') {
      console.error(
        'You need to provide a valueBuffer prop ' +
          'when using the buffer variant of LinearProgress.'
      )
    }
  }
  const thicknessStyle = {
    '--progressBarHeight': `${thickness}px`,
  } as React.CSSProperties

  return (
    <span
      className={clsx(
        styles.root,
        classes?.root,
        styles?.[`variant-${variant}`],
        classes?.[variant],
        variant !== 'buffer' && [styles?.[`variant-not${capitalize(variant)}`]],
        styles?.[`color-${color}`],
        className,
        sxFunc(sx)
      )}
      role="progressbar"
      {...rootProps}
      ref={ref}
      style={{ ...other.style, ...thicknessStyle }}
      {...other}
    >
      {variant === 'buffer' ? (
        <span
          className={clsx(
            styles.dashed,
            classes?.dashed,
            color === 'inherit' && styles['color-inherit']
          )}
        />
      ) : null}
      <span
        className={clsx(
          styles.progressBar1,
          styles?.[`variant-${variant}`],
          classes?.bar
        )}
        style={inlineStyles.bar1}
      />
      {variant === 'determinate' ? null : (
        <span
          className={clsx(
            styles.progressBar2,
            styles?.[`variant-${variant}`],
            variant !== 'buffer' && [
              styles?.[`variant-not${capitalize(variant)}`],
            ],
            color === 'inherit' && styles['color-inherit'],
            classes?.bar
          )}
          style={inlineStyles.bar2}
        />
      )}
    </span>
  )
})
